import { useCallback, useMemo } from 'react';
import { EditorView } from 'prosemirror-view';

import { PopperProps } from '../../types';
import { addList, getDomNode, toggleBlockquote } from '../../utils';
import { getMarkType, hideSelectionMenu, isMarkActive, toggleMark } from './commands';
import { LAST_USED_COLOR_KEY, SELECTION_MENU_BOTTOM_PADDING } from './consts';
import { MarkChangeActions, MarkTypesEnum } from './types';
import { adaptHref } from './utils';

export const useAnchorEl = (open: boolean, view: EditorView) => {
  return useMemo(() => {
    if (view && open) {
      return getDomNode(view, view.state.selection.$from.pos);
    }
    return null;
  }, [open, view]) as PopperProps['anchorEl'];
};

export const useOffset = (open: boolean, view: EditorView) => {
  return useMemo(() => {
    if (view && open) {
      const cursorPos = view.state.selection.$from.pos;
      const domNode = getDomNode(view, cursorPos);
      // @ts-ignore
      const getBoundingClientRect = domNode.getBoundingClientRect();
      const elementPos = view.coordsAtPos(cursorPos);
      // @see https://popper.js.org/docs/v1/#modifiers..offset
      return [
        elementPos.left - getBoundingClientRect.left,
        getBoundingClientRect.top - elementPos.top + SELECTION_MENU_BOTTOM_PADDING,
      ];
    } else {
      return [0, 0];
    }
  }, [view, view?.state.selection, open]);
};

export const usePopperOptions = (offset: number[]) => {
  return useMemo(() => {
    return {
      modifiers: [
        {
          name: 'flip',
          enabled: false,
        },
        // If you desire to apply offsets to your poppers in a way that may make
        // them overlap with their reference element, unfortunately, you will have
        // to disable the flip modifier. You can read more on this at this issue:
        // https://github.com/popperjs/popper-core/issues/373
        {
          name: 'offset',
          options: {
            offset,
          },
        },
      ],
    };
  }, [offset]);
};

export const useOnMarkChangeAction = (
  storage: any,
  view: EditorView,
  setIsLink: (bool: boolean) => void,
) => {
  return useCallback(
    (action: MarkChangeActions) => {
      if (!view) {
        return;
      }
      switch (action.type) {
        case MarkTypesEnum.BulletList:
        case MarkTypesEnum.NumberedList:
          addList(view, action.type);
          hideSelectionMenu(view.state, view.dispatch);
          break;
        case MarkTypesEnum.Blockquote:
          toggleBlockquote(view);
          hideSelectionMenu(view.state, view.dispatch);
          break;
        case MarkTypesEnum.Bold:
        case MarkTypesEnum.Italic:
        case MarkTypesEnum.Underline:
        case MarkTypesEnum.Code:
        case MarkTypesEnum.Strikethrough: {
          const markType = getMarkType(action.type, view.state.schema);
          if (!markType) return;
          toggleMark(markType)(view.state, view.dispatch);
          hideSelectionMenu(view.state, view.dispatch);
          break;
        }
        case MarkTypesEnum.Link: {
          if (!isMarkActive(MarkTypesEnum.Link, view.state)) {
            setIsLink(true);
          }
          break;
        }
        case MarkTypesEnum.Color: {
          if (!action.payload) return;
          const { color, backgroundColor } = action.payload;
          const markType = getMarkType('color', view.state.schema);
          if (!markType) return;

          toggleMark(markType, { backgroundColor, color }, true)(view.state, view.dispatch);
          hideSelectionMenu(view.state, view.dispatch);
          if (color) {
            storage?.setData(LAST_USED_COLOR_KEY, { color });
          }
          break;
        }
        default:
          break;
      }
    },
    [setIsLink, storage, view],
  );
};

export const useOnLinkSave = (view: EditorView, href: string) => {
  return useCallback(() => {
    if (view) {
      const markType = getMarkType('link', view.state.schema);
      if (!markType) return;

      toggleMark(markType, { href: adaptHref(href) }, true)(view.state, view.dispatch);
      hideSelectionMenu(view.state, view.dispatch);
    }
  }, [view, href]);
};

export const useOnLinkDelete = (view: EditorView) => {
  return useCallback(() => {
    if (view) {
      const markType = getMarkType('link', view.state.schema);
      if (!markType) return;

      toggleMark(markType)(view.state, view.dispatch);
      hideSelectionMenu(view.state, view.dispatch);
    }
  }, [view]);
};

export const useOnHrefChange = (view: EditorView, setHref: (url: string) => void) => {
  return useCallback(
    (event: any) => {
      if (view) {
        setHref(event.target.value.trim());
      }
    },
    [setHref, view],
  );
};
